import { ConversationMetadata, ConversationWithTasks } from '~/types/Conversations'
import { fetchWrapper } from '~/utils/fetchWrapper'

type UpdateConversationMetadata = {
  workspaceUuid: string
  conversationUuid: string
  metadata: ConversationMetadata
}

export interface ConversationsResponse {
  data: ConversationWithTasks[]
  next_cursor: number | null
  has_more: boolean
  data_hidden?: boolean
  data_hidden_reason?: 'credit_limit_exceeded'
  is_free_license?: boolean
  credits_used?: number
  credits_limit?: number
}

export async function updateConversationMetadata({
  workspaceUuid,
  conversationUuid,
  metadata
}: UpdateConversationMetadata) {
  const path = `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}/metadata`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(metadata)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update conversation metadata. Reason: ${reason}`)
  }
}

export const updateConversationTitle = async (payload: {
  workspaceUuid: string
  uuid: string
  title: string
}) => {
  const { workspaceUuid, uuid, ...body } = payload
  const path = `/api/workspace/${workspaceUuid}/conversation/${uuid}/title`
  const response = await fetchWrapper(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  })
  if (!response.ok) {
    const reason = await response.text()
    throw new Error(`Failed to update conversation title. Reason: ${reason}`)
  }
  const { data } = await response.json()
  return data as { title: string }
}

export const getConversationWithTasks = async (
  workspaceUuid: string,
  conversationUuid: string,
  opts?: { beforeMessageId?: number; messageLimit?: number }
): Promise<ConversationWithTasks> => {
  const params = new URLSearchParams()
  if (opts?.beforeMessageId != null) {
    params.set('before_message_id', String(opts.beforeMessageId))
  }
  if (opts?.messageLimit != null) {
    params.set('message_limit', String(opts.messageLimit))
  }
  const qs = params.toString()
  const path = `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}/detail${qs ? `?${qs}` : ''}`
  const data = await fetchWrapper.get<ConversationWithTasks>(path, {
    suppressErrorToast: true
  })
  return data
}

export const getConversationsPaginated = async (
  workspaceUuid: string,
  options?: { lastTimestamp?: number; query?: string; type?: 'chats' | 'tasks' }
): Promise<ConversationsResponse> => {
  const params = new URLSearchParams()
  const lastTimestamp = options?.lastTimestamp
  const query = options?.query?.trim()
  const type = options?.type
  if (lastTimestamp && lastTimestamp > 0) {
    params.append('last_timestamp', lastTimestamp.toString())
  }
  if (query && query.length > 0) {
    params.append('query', query)
  }
  if (type === 'chats' || type === 'tasks') {
    params.append('type', type)
  }
  params.append('page_size', '100')
  const path = `/api/workspace/${workspaceUuid}/conversations/paginated?${params.toString()}`

  const data = await fetchWrapper.get<ConversationsResponse>(path)
  return data
}

export const deleteConversation = async (workspaceUuid: string, conversationUuid: string) => {
  const path = `/api/workspace/${workspaceUuid}/conversation/${conversationUuid}`
  await fetchWrapper.delete(path)
}
